/**
 * Compute "streak" badges from a speaker's analysis history (oldest-first).
 *
 * Pure + dependency-free so the streak rules are unit-testable. All streaks are
 * "current runs" — counted from the most recent analysis backward — so a badge
 * only shows while the run is still alive.
 */
import { CATEGORY_KEYS, CATEGORY_LABELS, type TrendPoint } from "./types";

export type Streak = { id: string; text: string };

/** A category/overall score at or above this counts toward a threshold streak. */
export const CATEGORY_STREAK_MIN = 7; // 0–10 scale
export const OVERALL_STREAK_MIN = 70; // 0–100 scale

/** Count trailing elements (from the end) that satisfy the predicate. */
function trailingCount<T>(arr: T[], pred: (x: T) => boolean): number {
	let n = 0;
	for (let i = arr.length - 1; i >= 0; i--) {
		const item = arr[i];
		if (item !== undefined && pred(item)) n++;
		else break;
	}
	return n;
}

// The dashboard's trend points omit `meetingDate` (the trends route's carry it),
// so accept the minimal shape the streak rules actually read — both series fit.
export function computeStreaks(
	series: Pick<TrendPoint, "overallScore" | "categories">[],
): Streak[] {
	const out: Streak[] = [];
	if (series.length < 2) return out;

	// Consecutive recent analyses scoring at/above the overall bar.
	const overallRun = trailingCount(series, (p) => p.overallScore >= OVERALL_STREAK_MIN);
	if (overallRun >= 2) {
		out.push({
			id: "overall-threshold",
			text: `Overall ≥ ${OVERALL_STREAK_MIN} · ${overallRun} in a row`,
		});
	}

	// Consecutive recent analyses each higher than the one before (a rising run).
	let rising = 0;
	for (let i = series.length - 1; i > 0; i--) {
		const cur = series[i];
		const prev = series[i - 1];
		if (cur && prev && cur.overallScore > prev.overallScore) rising++;
		else break;
	}
	if (rising >= 2) out.push({ id: "rising", text: `Improving · ${rising + 1} meetings` });

	// Per-category threshold runs.
	for (const key of CATEGORY_KEYS) {
		const run = trailingCount(series, (p) => (p.categories[key] ?? 0) >= CATEGORY_STREAK_MIN);
		if (run >= 2) {
			out.push({
				id: `cat-${key}`,
				text: `${CATEGORY_LABELS[key]} ≥ ${CATEGORY_STREAK_MIN} · ${run} in a row`,
			});
		}
	}

	return out;
}
